import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import useNoIndex from '../hooks/useNoIndex';
import { getLeagueDetails, verifyLeaguePassword, updateChampionshipSeries, getSeriesList, getWidgetSubscriptionStatus, startWidgetSubscription, verifyWidgetSubscription, saveWidgetWebhook } from '../services/api';
import '../styles/fonts.css';
import '../styles/organizer.css';

const SESSION_KEY = (id) => `organizer_auth_${id}`;
const PWD_KEY = (id) => `organizer_pwd_${id}`;

function OrganizerPanel() {
  const { id } = useParams();
  const { t } = useTranslation();
  useNoIndex();
  const [searchParams] = useSearchParams();
  const [authenticated, setAuthenticated] = useState(false);
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [champTab, setChampTab] = useState('active');
  const [seriesInputs, setSeriesInputs] = useState({});
  const [seriesSaving, setSeriesSaving] = useState({});
  const [seriesSaved, setSeriesSaved] = useState({});
  const [seriesList, setSeriesList] = useState([]);

  // Widget subscription state
  const [subscription, setSubscription] = useState(undefined); // undefined=loading, null=none, obj=active
  const [subLoading, setSubLoading] = useState(null); // null | 'mp-3' | 'stripe-3' | etc
  const [subError, setSubError] = useState(null);
  const [subVerifying, setSubVerifying] = useState(false);
  const [widgetCheckout, setWidgetCheckout] = useState(null); // { checkoutUrl, provider, cacheKey }
  const [webhookInput, setWebhookInput] = useState('');
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookSaved, setWebhookSaved] = useState(false);
  const [copyStates, setCopyStates] = useState({});

  const loadSubscription = useCallback(async (pwd) => {
    try {
      const data = await getWidgetSubscriptionStatus(id, pwd);
      setSubscription(data.active ? data : null);
      if (data.active) setWebhookInput(data.discord_webhook_url || '');
    } catch {
      setSubscription(null);
    }
  }, [id]);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY(id)) === 'true') {
      setAuthenticated(true);
    }
    getLeagueDetails(id)
      .then(data => setLeague(data))
      .catch(() => setLeague(null))
      .finally(() => setLoading(false));
    getSeriesList().then(setSeriesList).catch(() => {});
  }, [id]);

  // Load subscription once authenticated
  useEffect(() => {
    if (!authenticated) return;
    const pwd = sessionStorage.getItem(PWD_KEY(id));
    if (pwd) loadSubscription(pwd);
  }, [authenticated, id, loadSubscription]);

  // Handle return from widget payment
  useEffect(() => {
    const widgetPaymentId = searchParams.get('widget_payment_id');
    const widgetSessionId = searchParams.get('widget_session_id');
    const widgetCacheKey = searchParams.get('widget_cache_key');
    if (!widgetPaymentId && !widgetSessionId && !widgetCacheKey) return;
    if (!sessionStorage.getItem(SESSION_KEY(id))) return;
    setSubVerifying(true);
    let verifyParam;
    if (widgetSessionId) verifyParam = `session_id=${widgetSessionId}`;
    else if (widgetPaymentId) verifyParam = `payment_id=${widgetPaymentId}`;
    else verifyParam = `cache_key=${widgetCacheKey}`;
    verifyWidgetSubscription(id, verifyParam)
      .then(data => {
        if (data.verified) {
          setSubscription(data);
          setWebhookInput(data.discord_webhook_url || '');
        }
      })
      .catch(() => {})
      .finally(() => setSubVerifying(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleLogin(e) {
    e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    try {
      const result = await verifyLeaguePassword(id, password);
      if (result.valid) {
        sessionStorage.setItem(SESSION_KEY(id), 'true');
        sessionStorage.setItem(PWD_KEY(id), password);
        setAuthenticated(true);
      }
    } catch {
      setAuthError(t('organizer.wrongPassword'));
    } finally {
      setAuthLoading(false);
    }
  }

  function getSeriesValue(champ) {
    const id = champ.championship_id;
    return id in seriesInputs ? seriesInputs[id] : (champ.series_name || '');
  }

  async function handleSeriesSave(champId) {
    const password = sessionStorage.getItem(PWD_KEY(id));
    const name = seriesInputs[champId] ?? '';
    setSeriesSaving(s => ({ ...s, [champId]: true }));
    try {
      await updateChampionshipSeries(champId, name, password);
      setSeriesSaved(s => ({ ...s, [champId]: true }));
      setTimeout(() => setSeriesSaved(s => ({ ...s, [champId]: false })), 2000);
    } finally {
      setSeriesSaving(s => ({ ...s, [champId]: false }));
    }
  }

  function handleLogout() {
    sessionStorage.removeItem(SESSION_KEY(id));
    sessionStorage.removeItem(PWD_KEY(id));
    setAuthenticated(false);
    setPassword('');
  }

  async function handleBuyPlan(planMonths, provider) {
    const pwd = sessionStorage.getItem(PWD_KEY(id));
    const key = `${provider}-${planMonths}`;
    setSubLoading(key);
    setSubError(null);
    try {
      const data = await startWidgetSubscription(id, pwd, planMonths, provider);
      if (provider === 'stripe') {
        window.location.href = data.checkout_url;
      } else {
        setWidgetCheckout({ checkoutUrl: data.checkout_url, provider, cacheKey: data.cache_key });
        setSubLoading(null);
      }
    } catch {
      setSubLoading(null);
      setSubError(t('widget.errorStart'));
    }
  }

  function handleVerifyByCacheKey() {
    if (!widgetCheckout?.cacheKey) return;
    setSubVerifying(true);
    setWidgetCheckout(null);
    verifyWidgetSubscription(id, `cache_key=${widgetCheckout.cacheKey}`)
      .then(data => {
        if (data.verified) {
          setSubscription(data);
          setWebhookInput(data.discord_webhook_url || '');
        } else {
          setSubError(t('widget.paymentNotConfirmed'));
        }
      })
      .catch(() => setSubError(t('widget.paymentNotConfirmed')))
      .finally(() => setSubVerifying(false));
  }

  async function handleSaveWebhook() {
    const pwd = sessionStorage.getItem(PWD_KEY(id));
    setWebhookSaving(true);
    try {
      await saveWidgetWebhook(id, pwd, webhookInput);
      setWebhookSaved(true);
      setTimeout(() => setWebhookSaved(false), 2500);
    } catch {} finally {
      setWebhookSaving(false);
    }
  }

  function handleCopy(key, text) {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopyStates(s => ({ ...s, [key]: true }));
    setTimeout(() => setCopyStates(s => ({ ...s, [key]: false })), 2000);
  }

  if (loading) return (
    <div className="OrganizerPage">
      <Header />
      <main><div className="organizer-content"><p className="organizer-loading">{t('common.loading')}</p></div></main>
      <Footer />
    </div>
  );

  if (!league) return (
    <div className="OrganizerPage">
      <Header />
      <main><div className="organizer-content"><p className="organizer-error">{t('organizer.leagueNotFound')}</p></div></main>
      <Footer />
    </div>
  );

  if (!authenticated) {
    return (
      <div className="OrganizerPage">
        <Header />
        <main>
          <div className="organizer-content">
            <div className="organizer-form-card organizer-login-card">
              <div className="organizer-login-header">
                {league.small_logo && (
                  <img src={league.small_logo} alt={league.name} className="organizer-login-logo" />
                )}
                <div>
                  <p className="organizer-login-label">{t('organizer.panelTitle')}</p>
                  <h2 className="organizer-login-name">{league.name}</h2>
                </div>
              </div>

              <form onSubmit={handleLogin} className="organizer-form">
                <label className="form-label">
                  {t('organizer.leaguePassword')}
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={t('organizer.passwordPlaceholder')}
                    className="form-input"
                    autoFocus
                    required
                  />
                </label>
                {authError && <p className="form-error">{authError}</p>}
                <button type="submit" className="btn-primary" disabled={authLoading}>
                  {authLoading ? t('organizer.verifying') : t('organizer.enter')}
                </button>
              </form>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const allChampionships = (league?.championships) || [];
  const activeChamps   = allChampionships.filter(c => c.active);
  const finishedChamps = allChampionships.filter(c => !c.active);
  const championships  = champTab === 'active' ? activeChamps : finishedChamps;
  const isWidgetTab    = champTab === 'widget';

  return (
    <div className="OrganizerPage">
      <Header />
      <main>
        <div className="organizer-content">

          <div className="organizer-panel-header">
            <div className="organizer-panel-identity">
              {league.small_logo && (
                <img src={league.small_logo} alt={league.name} className="organizer-panel-logo" />
              )}
              <div>
                <p className="organizer-panel-label">{t('organizer.panelTitle')}</p>
                <h1 className="organizer-panel-name">{league.name}</h1>
              </div>
            </div>
            <button onClick={handleLogout} className="btn-secondary btn-sm">
              {t('organizer.logout')}
            </button>
          </div>

          <div className="organizer-section">
            <div className="box-header">
              <h3>{t('common.championships')}</h3>
              <span className="box-header-count">{allChampionships.length}</span>
            </div>

            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 16 }}>
              {[
                { key: 'active',   label: t('organizer.tabActive'),   count: activeChamps.length },
                { key: 'finished', label: t('organizer.tabFinished'), count: finishedChamps.length },
                { key: 'widget',   label: t('widget.sectionTitle'),   count: null },
              ].map(tab => (
                <button key={tab.key} type="button"
                  style={{
                    padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
                    color: champTab === tab.key ? 'var(--accent, #e8c84a)' : 'rgba(255,255,255,0.55)',
                    borderBottom: champTab === tab.key ? '2px solid var(--accent, #e8c84a)' : '2px solid transparent',
                    fontWeight: champTab === tab.key ? 600 : 400,
                    fontSize: '0.95rem', marginBottom: -1, transition: 'color 0.15s',
                  }}
                  onClick={() => setChampTab(tab.key)}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span style={{ marginLeft: 6, fontSize: '0.78rem', opacity: 0.6 }}>({tab.count})</span>
                  )}
                </button>
              ))}
            </div>

            {/* Add championship button — manual leagues only */}
            {!isWidgetTab && league.is_manual && (
              <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Link
                  to={`/leagues/${id}/organizer/create-championship`}
                  className="btn-primary btn-sm"
                  style={{ textDecoration: 'none' }}
                >
                  + {t('organizer.addChampionship')}
                </Link>
              </div>
            )}

            {/* Championships list (active/finished tabs) */}
            {!isWidgetTab && (championships.length === 0 ? (
              <p className="organizer-empty">{t('organizer.noChampionshipsFound')}</p>
            ) : (
              <div className="organizer-champ-list">
                {championships.map(champ => (
                  <div key={champ.championship_id} className="organizer-champ-card">
                    <div className="organizer-champ-info">
                      <p className="organizer-champ-name">{champ.name}</p>
                      <p className="organizer-champ-meta">
                        {(champ.sessions || []).length} {t('organizer.stages')}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        <label style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
                          {t('common.series')}:
                        </label>
                        <select
                          className="form-input"
                          style={{ fontSize: '0.82rem', padding: '3px 8px', flex: 1, minWidth: 0 }}
                          value={getSeriesValue(champ)}
                          onChange={e => setSeriesInputs(s => ({ ...s, [champ.championship_id]: e.target.value }))}
                        >
                          <option value="">{t('organizer.seriesPlaceholder')}</option>
                          {seriesList.map(s => (
                            <option key={s.id} value={s.name}>{s.name}</option>
                          ))}
                        </select>
                        <button
                          className="btn-primary btn-sm"
                          style={{ whiteSpace: 'nowrap' }}
                          disabled={seriesSaving[champ.championship_id]}
                          onClick={() => handleSeriesSave(champ.championship_id)}
                        >
                          {seriesSaved[champ.championship_id] ? '✓' : seriesSaving[champ.championship_id] ? '...' : t('common.save')}
                        </button>
                      </div>
                    </div>
                    <div className="organizer-champ-actions">
                      <Link to={`/championships/${champ.championship_id}`} className="btn-secondary btn-sm">
                        {t('organizer.viewPublic')}
                      </Link>
                      <Link
                        to={`/leagues/${id}/organizer/championships/${champ.championship_id}`}
                        className="btn-primary btn-sm"
                      >
                        {t('organizer.configure')}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* Widget tab content */}
            {isWidgetTab && (
            <div>
            {/* MP payment interstitial */}
            {widgetCheckout && (
              <div style={{ background: '#21262d', border: '1px solid #30363d', borderRadius: 10, padding: '28px 24px', textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: '2rem', marginBottom: 10 }}>💳</div>
                <p style={{ color: '#e6edf3', fontWeight: 600, marginBottom: 20 }}>{t('widget.paymentReady')}</p>
                <a
                  href={widgetCheckout.checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', padding: '12px 28px', background: '#009ee3', color: '#fff', borderRadius: 8, fontWeight: 700, textDecoration: 'none', marginBottom: 20 }}
                >
                  {t('marketplace.paymentGoToMp')}
                </a>
                <div style={{ borderTop: '1px solid #30363d', paddingTop: 18 }}>
                  <p style={{ color: '#8b949e', fontSize: '13px', marginBottom: 10 }}>{t('marketplace.paymentAlreadyPaidHint')}</p>
                  <button
                    onClick={handleVerifyByCacheKey}
                    style={{ padding: '8px 20px', background: 'transparent', border: '1px solid #4caf50', borderRadius: 6, color: '#4caf50', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                  >
                    ✓ {t('marketplace.paymentAlreadyPaid')}
                  </button>
                </div>
              </div>
            )}
            {subVerifying && (
              <p style={{ color: '#9ba3ab', fontSize: '0.88rem' }}>{t('widget.verifying')}</p>
            )}
            {subError && (
              <div style={{ background: '#3d2020', color: '#e07070', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: '0.85rem' }}>
                {subError}
              </div>
            )}

            {subscription === undefined && !subVerifying ? (
              <p style={{ color: '#9ba3ab', fontSize: '0.85rem' }}>{t('widget.loading')}</p>
            ) : subscription ? (
              /* ── Active subscription ── */
              <div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 24 }}>
                  <div style={{ background: '#1e2d1e', border: '1px solid #3a5a3a', borderRadius: 8, padding: '14px 20px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6e7681', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t('widget.plan')}</div>
                    <div style={{ fontWeight: 700, color: '#4caf50' }}>{t(`widget.plan${subscription.plan_months}`)}</div>
                  </div>
                  <div style={{ background: '#1e2d1e', border: '1px solid #3a5a3a', borderRadius: 8, padding: '14px 20px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6e7681', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t('widget.activeUntil')}</div>
                    <div style={{ fontWeight: 700, color: '#e6edf3' }}>{new Date(subscription.end_date).toLocaleDateString('pt-BR')}</div>
                  </div>
                </div>

                {/* Discord webhook config */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 6 }}>{t('widget.webhookTitle')}</div>
                  <p style={{ fontSize: '0.82rem', color: '#9ba3ab', marginBottom: 10 }}>{t('widget.webhookDesc')}</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="url"
                      className="form-input"
                      style={{ flex: 1, fontSize: '0.85rem' }}
                      placeholder={t('widget.webhookPlaceholder')}
                      value={webhookInput}
                      onChange={e => setWebhookInput(e.target.value)}
                    />
                    <button
                      className="btn-primary btn-sm"
                      disabled={webhookSaving}
                      onClick={handleSaveWebhook}
                    >
                      {webhookSaved ? '✓' : webhookSaving ? '...' : t('widget.webhookSave')}
                    </button>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#6e7681', marginTop: 6 }}>{t('widget.webhookHowTo')}</p>
                </div>

                {/* Snippets per championship */}
                <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 8 }}>{t('widget.exportTitle')}</div>
                <p style={{ fontSize: '0.82rem', color: '#9ba3ab', marginBottom: 12 }}>{t('widget.exportDesc')}</p>
                {allChampionships.map(champ => {
                  const base = window.location.origin;
                  const token = subscription.widget_token;
                  const cid = champ.championship_id;
                  const frame = (id, path, height) => `<iframe id="${id}" src="${base}${path}?token=${token}" width="100%" height="${height}" scrolling="no" frameborder="0" style="border:none;"></iframe><script>(function(){var f=document.getElementById('${id}');window.addEventListener('message',function(e){if(e.source===f.contentWindow&&e.data&&e.data.type==='celeritas-widget-resize'){f.style.height=e.data.height+'px';}});})();</script>`;
                  const snippets = [
                     { key: `standings-${cid}`, label: t('widget.iframeStandings'), value: frame(`celeritas-standings-${cid}`, `/widget/leagues/${id}/championships/${cid}/standings`, 620) },
                     { key: `schedule-${cid}`, label: t('widget.iframeSchedule'), value: frame(`celeritas-schedule-${cid}`, `/widget/leagues/${id}/championships/${cid}/schedule`, 520) },
                     { key: `results-${cid}`, label: t('widget.iframeResults'), value: frame(`celeritas-results-${cid}`, `/widget/leagues/${id}/championships/${cid}/results`, 900) },
                    { key: `api-${cid}`, label: t('widget.jsonApi'), value: `${base.replace('3000', '8000')}/api/widget/${token}/championships/${cid}/` },
                  ];
                  return (
                    <div key={cid} style={{ marginBottom: 20, background: '#1b2127', borderRadius: 8, padding: '14px 16px' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 10 }}>{champ.name}</div>
                      {snippets.map(s => (
                        <div key={s.key} style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: '0.75rem', color: '#6e7681', marginBottom: 4 }}>{s.label}</div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                            <code style={{ flex: 1, background: '#161b22', borderRadius: 4, padding: '6px 10px', fontSize: '0.72rem', color: '#c0c8d0', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                              {s.value}
                            </code>
                            <button
                              className="btn-secondary btn-sm"
                              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                              onClick={() => handleCopy(s.key, s.value)}
                            >
                              {copyStates[s.key] ? t('widget.copied') : t('widget.copyBtn')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {/* Renew plan */}
                <div style={{ marginTop: 8 }}>
                  <WidgetPlanPicker t={t} subLoading={subLoading} onBuy={handleBuyPlan} label={t('widget.renewBtn')} />
                </div>
              </div>
            ) : (
              /* ── No subscription ── */
              <div>
                <p style={{ fontSize: '0.88rem', color: '#9ba3ab', marginBottom: 6 }}>{t('widget.noSubTitle')}</p>
                <p style={{ fontSize: '0.83rem', color: '#6e7681', marginBottom: 20 }}>{t('widget.noSubText')}</p>
                <WidgetPlanPicker t={t} subLoading={subLoading} onBuy={handleBuyPlan} />
              </div>
            )}
            </div>
            )}

          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}

function WidgetPlanPicker({ t, subLoading, onBuy, label }) {
  const plans = [
    { months: 3,  brl: t('widget.price3Brl'),  usd: t('widget.price3Usd'),  label: t('widget.plan3') },
    { months: 6,  brl: t('widget.price6Brl'),  usd: t('widget.price6Usd'),  label: t('widget.plan6') },
    { months: 12, brl: t('widget.price12Brl'), usd: t('widget.price12Usd'), label: t('widget.plan12') },
  ];
  return (
    <div>
      {label && <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 12 }}>{label}</div>}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {plans.map(plan => (
          <div key={plan.months} style={{ background: '#21262d', border: '1px solid #30363d', borderRadius: 10, padding: '16px 18px', minWidth: 160, flex: '1' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>{plan.label}</div>
            <div style={{ color: '#e8c84a', fontWeight: 700, fontSize: '1.1rem', marginBottom: 2 }}>{plan.brl}</div>
            <div style={{ color: '#6e7681', fontSize: '0.78rem', marginBottom: 12 }}>{plan.usd}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button
                className="btn-primary btn-sm"
                style={{ background: '#009ee3', borderColor: '#009ee3' }}
                disabled={subLoading !== null}
                onClick={() => onBuy(plan.months, 'mercadopago')}
              >
                {subLoading === `mercadopago-${plan.months}` ? '...' : t('widget.payWithMp')}
              </button>
              <button
                className="btn-primary btn-sm"
                style={{ background: '#635bff', borderColor: '#635bff' }}
                disabled={subLoading !== null}
                onClick={() => onBuy(plan.months, 'stripe')}
              >
                {subLoading === `stripe-${plan.months}` ? '...' : t('widget.payWithStripe')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default OrganizerPanel;
